import React, { useState, useEffect } from 'react';
import { X, HelpCircle, ChevronDown, ChevronUp, ExternalLink, BookOpen } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { helpContent, generalFaqs } from './helpContent';

// ============================================
// HELP BUTTON - Floating ? icon for each module
// ============================================
export function HelpButton({ moduleId, onClick, style = {} }) {
  const { theme } = useTheme();
  
  return (
    <button
      onClick={onClick}
      title="Help"
      style={{
        background: theme.colors.bg.tertiary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        color: theme.colors.text.secondary,
        ...style
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = theme.colors.brand.primary;
        e.currentTarget.style.color = 'white';
        e.currentTarget.style.borderColor = theme.colors.brand.primary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = theme.colors.bg.tertiary;
        e.currentTarget.style.color = theme.colors.text.secondary;
        e.currentTarget.style.borderColor = theme.colors.border.primary;
      }}
    >
      <HelpCircle size={16} />
    </button>
  );
}

// ============================================
// FAQ ACCORDION ITEM
// ============================================
function FaqItem({ question, answer, isOpen, onToggle }) {
  const { theme } = useTheme();
  
  return (
    <div style={{
      borderBottom: `1px solid ${theme.colors.border.primary}`,
      marginBottom: '0'
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '16px 0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          textAlign: 'left'
        }}
      >
        <span style={{
          color: theme.colors.text.primary,
          fontSize: '14px',
          fontWeight: '500',
          flex: 1
        }}>
          {question}
        </span>
        {isOpen ? (
          <ChevronUp size={18} style={{ color: theme.colors.text.tertiary, flexShrink: 0 }} />
        ) : (
          <ChevronDown size={18} style={{ color: theme.colors.text.tertiary, flexShrink: 0 }} />
        )}
      </button>
      
      {isOpen && (
        <div style={{
          padding: '0 0 16px 0',
          color: theme.colors.text.secondary,
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          {answer}
        </div>
      )}
    </div>
  );
}

// ============================================
// HELP PANEL - Slide-out panel with module help
// ============================================
export function HelpPanel({ moduleId, isOpen, onClose }) {
  const { theme } = useTheme();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  
  const content = helpContent[moduleId] || helpContent['dashboard'];
  
  // Reset FAQ state when panel opens/closes
  useEffect(() => {
    if (!isOpen) {
      setOpenFaqIndex(null);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Overlay */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease'
        }}
      />
      
      {/* Panel */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '400px',
        maxWidth: '100vw',
        background: theme.colors.bg.primary,
        borderLeft: `1px solid ${theme.colors.border.primary}`,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideInRight 0.3s ease'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${theme.colors.border.primary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>{content.icon}</span>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '700',
                color: theme.colors.text.primary
              }}>
                {content.title}
              </h2>
              <span style={{
                fontSize: '12px',
                color: theme.colors.text.tertiary
              }}>
                Help & Documentation
              </span>
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: theme.colors.text.secondary
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Description */}
          <p style={{
            color: theme.colors.text.secondary,
            fontSize: '14px',
            lineHeight: '1.6',
            margin: '0 0 24px 0'
          }}>
            {content.description}
          </p>
          
          {/* Features */}
          {content.features && content.features.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{
                fontSize: '13px',
                fontWeight: '700',
                color: theme.colors.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: '0 0 12px 0'
              }}>
                Key Features
              </h3>
              <ul style={{
                margin: 0,
                padding: '0 0 0 20px',
                color: theme.colors.text.primary,
                fontSize: '14px',
                lineHeight: '1.8'
              }}>
                {content.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* FAQs */}
          {content.faqs && content.faqs.length > 0 && (
            <div>
              <h3 style={{
                fontSize: '13px',
                fontWeight: '700',
                color: theme.colors.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: '0 0 12px 0'
              }}>
                Frequently Asked Questions
              </h3>
              
              {content.faqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFaqIndex === index}
                  onToggle={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${theme.colors.border.primary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <a
            href="/help"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: theme.colors.brand.primary,
              fontSize: '14px',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            <BookOpen size={16} />
            View all help topics
          </a>
          
          <a
            href="mailto:support@bidoryx.com"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: theme.colors.text.secondary,
              fontSize: '13px',
              textDecoration: 'none'
            }}
          >
            <ExternalLink size={14} />
            Contact support
          </a>
        </div>
        
        {/* Animation styles */}
        <style>{`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default HelpPanel;
